<script lang="ts" setup>
import { ref, computed, watch, nextTick } from 'vue';
import type { PropType } from 'vue';
import { useFocusWithin, useFocus, useDebounceFn } from '@vueuse/core';
interface SelectOption {
    id: string;
    key: string;
    value: string;
    selected?: boolean;
}
let props = defineProps({
    noBorders: {
        type: Boolean,
        required: false,
        default: false
    },
    customLabel:{
        type: Boolean,
        required: false,
        default: false
    },
    label: {
        type: String,
        required: false,
        default: '',
    },
    ariaLabelledBy:{
        type: String,
        required: false,
        default(){
            return `select_${Math.random().toString(36).substring(2, 15)}`;
        }
    },
    'ariaActiveDescendant':{
        type: String,
        required: false
    },
    options: { 
        type: Array as PropType<SelectOption[]>,
        required: false,
        default(){
            return [
            ]
        }
    },
    multiple: {
        type: Boolean,
        required: false,
        default: false
    },
    upTo: {
        type: Number,
        required: false,
        default: Infinity
    },
    allowMore: {
        type: Boolean,
        required: false,
        default: false
    },
    allowMorePlaceholder: {
        type: String,
        required: false,
        default: 'Add more'
    },
    readonly: {
        type: Boolean,
        required: false,
        default: false  
    },
    name: {
        type: String,
        required: false,
        default: 'custom-select',
    },
    allowBackendSearch: {
        type: Boolean,
        required: false,
        default: false  
    },
    onBackendSearch: {
      type: Function,
      required: false
    },
    viewAll: {
        type: Boolean,
        required: false,
        default: true
    },
    emptyMessage: {
        type: String,
        required: false,
        default: 'No options available'
    },
    error: {
        type: Boolean,
        required: false,
        default: false
    }
})


let selectedOption = defineModel({
    type: Object as PropType<SelectOption>,
    default(){
        return {
            key: "",
            value: ""
        }
    }
});

let selectedOptions = defineModel('selectedOptions', {
    type: Array as PropType<SelectOption[]>,
    default(){
        return []
    }
})

const searchOptions = ref();
const { focused:searchOptionsFocused } = useFocus(searchOptions);

let open = () => {
    if(props.readonly) {
        return;
    }
    details.value?.setAttribute?.('open', '');
    if(searchOptions.value) {
        searchOptionsFocused.value = true;
    }else{
        details.value.querySelector(`#option_${filteredOptions.value?.[0]?.key}_0`)?.focus();
    }
} 

let close = () => {
    viewMoreOptions.value = props.viewAll;
    onViewMore.value = false;
    details.value?.removeAttribute?.('open');
}
let details = ref();

const { focused:detailsFocused } = useFocusWithin(details);
watch(detailsFocused, (newVal) => {
  if(!newVal){
    details.value?.removeAttribute?.('open');
    if(props.allowMore){
        addOption();
    }
  }
})

let toggle = ()=>{
    // if details has attribute open, remove it, else add it
    if(details.value?.hasAttribute?.('open')){
        close();
    }else{
        open();
    }
}
let handleSelect = (option: SelectOption)=>{
    if(props.readonly) {
        return;
    }
    if(props.multiple){
        let index = selectedOptions.value.findIndex((opt)=> opt.key === option.key);
        if(index > -1){
            selectedOptions.value.splice(index, 1);
        }else{
            // if the number of selected options is less than the upTo prop, else remove the first option
            if(selectedOptions.value.length < props.upTo){
                selectedOptions.value.push(option);
            }else{
                selectedOptions.value.shift();
                selectedOptions.value.push(option);
            }

        }

        nextTick(()=>{
            details.value?.setAttribute?.('open', '');
        })
    }else{
            selectedOption.value = option;
            details.value?.removeAttribute?.('open');
    }
}

let longName = computed(()=>{
    return selectedOption.value?.value?.length > 10;
});

let isSelected = (option: SelectOption)=>{
if(props.multiple){
        return selectedOptions.value.findIndex((opt)=> opt.key === option.key) > -1;
    }else{
        return selectedOption.value.key === option.key;
    }
}

let formattedSelected = computed(()=>{
    if(props.multiple){
        return selectedOptions.value.map((option)=> option.value).join(', ');
    }else{
        return selectedOption.value.value;
    }
})

let newOption = ref('');
let preventBubble = (event)=>{
    event.stopPropagation();
}
let addedOptions = ref([]);

let addOption = ()=>{
    if(newOption.value.trim() !== ''){
        let option = {
            key: newOption.value,
            value: newOption.value
        }

        // prevent duplicate entries, move recently added to front
        addedOptions.value = [
            option, ...addedOptions.value.filter((opt)=> opt.key !== option.key)
        ]
        selectedOptions.value = [
            option, ...selectedOptions.value.filter((opt)=> opt.key !== option.key)
        ]
    

        newOption.value = '';
    }
}

let totalOptions = computed(()=>{
    return [...props.options, ...addedOptions.value];
})

let hasAlotOfOptions = computed(()=>{
    return totalOptions.value.length > 5;
})

let hasOptions = computed(()=>{
    return totalOptions.value.length > 0;
})

const debouncedSearch = useDebounceFn((query) => {
    props.onBackendSearch(query);
}, 300);


let optionSearch = ref('');

let filteredOptions = computed(()=>{
    if(optionSearch.value.trim() === ''){
        if(!viewMoreOptions.value){
            return totalOptions.value.slice(0, 5).map((option => ({ ...option, selected: isSelected(option) }))); // return a shallow copy to avoid mutating the original array
        }
        return totalOptions.value.map((option => ({ ...option, selected: isSelected(option) }))); // return a shallow copy to avoid mutating the original array
    }

   let opts = totalOptions.value.filter((option)=> option.value.toLowerCase().includes(optionSearch.value.toLowerCase()));
   if(!viewMoreOptions.value){
        return opts.slice(0, 5);
   }
   return opts;
})

let addSearchedOption = ()=>{
    if(optionSearch.value.trim() !== ''){
        let option = {
            key: optionSearch.value,
            value: optionSearch.value
        }

        // if option already exists, do not add it
        let optionIndex = totalOptions.value.findIndex((opt)=> opt.value.toLowerCase() === option.value.toLowerCase())
        if(optionIndex == -1 && props.allowMore){
            addedOptions.value.push(option);
            optionIndex = totalOptions.value.length - 1;
        }else if(optionIndex == -1){
            return;
        }
        if(props.multiple){
            if(selectedOptions.value.findIndex((opt)=> opt.value.toLowerCase() === option.value.toLowerCase()) == -1){
                selectedOptions.value.push(totalOptions.value[optionIndex]);
            }else{
                selectedOptions.value = selectedOptions.value.filter((opt)=> opt.value.toLowerCase() !== option.value.toLowerCase());
            }
        }else{
            selectedOption.value = totalOptions.value[optionIndex];
        }

        optionSearch.value = '';
    }
}

const traverseOptions = (i)=>{
    if(i < 0){
        searchOptionsFocused.value = true;
    }else{
        if( i >= filteredOptions.value.length && hasAlotOfOptions.value){
            details.value.querySelector(`#option_view_more`)?.focus();
            onViewMore.value = true;
            return;
        }
        onViewMore.value = false;
        details.value.querySelector(`#option_${filteredOptions.value[i]?.key}_${i}`)?.focus();
    }
}

let selectedValue = ref(selectedOption.value?.key || '');

let syncStateFromSelect = () => {
    const matchingOption = totalOptions.value.find(option => option.key === selectedValue.value);
    if (matchingOption) {
        selectedOption.value = matchingOption;
    }
};

watch(selectedOption, (newOption) => {
    if (newOption) {
        selectedValue.value = newOption.key;
    }
});

watch(optionSearch, (value) => {
    if (props.allowBackendSearch && value.trim() !== '') {
        debouncedSearch(value);
    }
});

let viewMoreOptions = ref(props.viewAll);
let onViewMore = ref(false);
let viewMore = (event)=>{
    preventBubble(event);
    viewMoreOptions.value = !viewMoreOptions.value;
    nextTick(()=>{
        details.value?.setAttribute?.('open', '');
        details.value.querySelector(`#option_${filteredOptions.value[5].key}_5`)?.focus?.();
    })
}


let isExpanded = computed(()=>{
    return details.value?.hasAttribute?.('open');
})

</script>
<template>
    <details ref="details" @click.prevent="toggle" @focus="open" :tabindex="readonly || detailsFocused ? -1 : 0" :class="{
        'custom-select-wrapper': true,
        'no-borders-select': noBorders,
        'readonly': readonly,
        'invalid-state-input': error
    }">
        <!-- Hidden Input for Chrome Autofill -->
        <select 
            v-model="selectedValue"
            :name="name"
            class="hidden-select"
            @change="syncStateFromSelect"
        >
            <option v-for="(option, i) in options" :key="`${option.id}_${i}`" :value="option.key">
                {{ option.value }}
            </option>
        </select>
        <!-- Custom Select -->
        <summary aria-haspopup="listbox" :aria-expanded="isExpanded" tabindex="-1">
            <div v-if="customLabel" :class="{
                'custom-select-label': true,
                'xs-label': longName
            }">
                <p :id="ariaLabelledBy" class="label" v-if="label.length">{{ label }}</p>
                <slot name="label" v-bind="{ selected: multiple ? selectedOptions : selectedOption}"></slot>               
            </div>
            <div v-else class="custom-select-label">
                <p :id="ariaLabelledBy" class="label">{{ label }}</p>
                <p class="selected-option">{{ formattedSelected }}</p>
            </div>
        </summary>
        <ul class="custom-select-options"
            :style="{
                'min-width': 'fit-content',
                'max-height': (viewAll || viewMoreOptions) && filteredOptions.length > 15 ? '14rem' : 'initial'
            }"
            role="listbox"
            :aria-labelledby="ariaLabelledBy"
            :aria-activedescendant="ariaActiveDescendant"
            ref="optionsList"
        >
            <li @click.stop="preventBubble" v-if="(hasOptions && hasAlotOfOptions) || allowMore" class="custom-select-option">
                <input 
                    v-if="allowMore" ref="searchOptions"
                    @keypress.enter="addOption"
                    @keydown.down.prevent="traverseOptions(0)" 
                    type="text" 
                    v-model.trim="newOption" 
                    :disabled="selectedOptions.length == upTo"
                    :placeholder="allowMorePlaceholder" 
                    />

                <input 
                    v-else-if="hasAlotOfOptions" ref="searchOptions" :tabindex="detailsFocused ? -1 : 0"
                    @keypress.enter="addSearchedOption"
                    @keydown.down.prevent="traverseOptions(0)" 
                    type="text" 
                    v-model.trim="optionSearch" 
                    placeholder="Search" 
                    />
            </li>
            <li @keydown.enter="handleSelect(option)" @click.stop="handleSelect(option)" @keydown.tab="($event)=>(!$event.shiftKey && handleSelect(option))"
                @keydown.up.prevent="traverseOptions(i-1)" @keydown.down.prevent="traverseOptions(i+1)"
                :class="{
                'custom-select-option': true,
                'selected': isSelected(option),
                'removable': isSelected(option) && allowMore
            }" v-for="(option, i) in filteredOptions" :key="`${option.key}_${option.selected ? 'selected' : 'unselected'}_${i}`" :id="`option_${option.key}_${i}`" role="option" tabindex="-1" :aria-selected="isSelected(option)">
        {{ option.value }}
            </li>
            <li id="option_view_more"  @keydown.enter="viewMore" @click.stop="viewMore"
            @keydown.up.prevent="traverseOptions(filteredOptions.length -1)" @keydown.down.prevent="traverseOptions(0)" :class="{
                'custom-select-option': true,
                'selected': onViewMore
        }" v-if="!viewAll && hasAlotOfOptions && !viewMoreOptions" role="option" tabindex=" -1" :aria-selected="onViewMore">
                View more options
            </li>

            <li   v-if="filteredOptions.length == 0" class="custom-select-option">
                {{ props.emptyMessage }}
            </li>

        </ul>
    </details>
</template>
<style lang="scss" scoped>
.hidden-select {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    height: 0;
    width: 0;
}
</style>